<script lang="ts" setup>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserVariablesStore } from '../../store/userVariables';
import { useStyleStore } from '../../store/styleStore';

const userVariablesStore = useUserVariablesStore();
const router = useRouter();
const route = useRoute();
const defaultLanguage = ref('en');
const currentLanguage = ref<string>(route.query.lang as string || defaultLanguage.value);
const isLoading = ref(true); // Loading State
const languageCodes = ref<string[]>([]);
const translation = computed(() => userVariablesStore.currentLanguageData);
const notAiLabInstance = ref(false)
const styleStore = useStyleStore();
const styles = ref(styleStore.getStyles());
// @ts-ignore
const assetsUrl = ref(import.meta.env.VITE_CLIENT_ASSETS_URL);
const allowedHostnames = [
  'www.tuev-risk-navigator.ai',
  'staging.tuev-risk-navigator.ai',
];


const checkInstance = () => {
  const currentHostname = window.location.hostname.trim().toLowerCase(); 
  console.log(currentHostname);

  notAiLabInstance.value = allowedHostnames.includes(currentHostname);
};


const toggleLanguage = async (newLang: string) => {
  router.push({ query: { ...route.query, lang: newLang } });
  await userVariablesStore.loadLanguageData(newLang);
  userVariablesStore.setLanguage(newLang);
  currentLanguage.value = newLang;
};


function openInNewTab(url: string) {
    window.open(url, '_blank').focus();
}

// this is a function that probably gets removed because it makes to much editable
const applyDynamicCSS = (css: string) => {
    const styleTag = document.createElement('style');
    styleTag.textContent = css;
    document.head.appendChild(styleTag);
};

const initPage = async () => {
  await userVariablesStore.loadLanguageData(currentLanguage.value || defaultLanguage.value);
  languageCodes.value = userVariablesStore.allLanguageCodes;
  isLoading.value = false;
};



watch(
  () => route.query.lang,
  async (newLang: any) => {
    if (newLang) {
      currentLanguage.value = newLang;
      await toggleLanguage(newLang);
    }
  }
);

// not best practice but dynamic subtitle break after "Risk" for smaller screens
const updateBreak = () => {
    const appSubtitle = translation.value?.staticTexts?.appSubtitle;
    if (appSubtitle) {
        const newSubtitle = window.innerWidth < 678 
            ? appSubtitle.replace("Risk ", "Risk<br> ") 
            : appSubtitle.replace("Risk<br> ", "Risk ");
        translation.value.staticTexts.appSubtitle = newSubtitle;
    }
};

onMounted(async () => {
  await initPage();
  userVariablesStore.setLanguage(currentLanguage.value || defaultLanguage.value);
  checkInstance();
  updateBreak();
  window.addEventListener("resize", updateBreak);
});

onUnmounted(() => {
    window.removeEventListener("resize", updateBreak);
  });

watch(() => translation.value?.staticTexts?.appSubtitle, updateBreak);
</script>



<template>
    <div v-if="!isLoading"  class="container-fluid text-white" id="main-container">
        <div class="row align-items-center">
            <div class="col-3 text-center justify-content-center align-items-center z-3">
                <span class="developedBy" v-if="!notAiLabInstance">Developed By</span><br>
        <img v-if="!notAiLabInstance" id="labLogo" 
        :src="assetsUrl + (styles?.developedByLogo?.value || '/img/TUEV-AILAB-Logo_weiss.png')" 
         alt="TÜV AI LAB"
        @click="openInNewTab('https://www.tuev-lab.ai');" class="p-2 clickable">
            </div>
            <div class="col-6 text-center">
                <!-- width changed from 120 to 170 for hessen-->
                <img id="logo" @click="openInNewTab(styles?.logoLink?.value)" :src="assetsUrl + styles?.logo?.value" width="120" alt="Page Logo" class="clickable">
            </div>
            <div class="col-3 text-center justify-content-center align-items-center z-3">
                <div class="row">
                    <div class="col-12 p-0">
                        <span v-for="(code, index) in languageCodes" :key="index" class="language-button">
                        <a href="#" class="text-decoration-none mb-3 pr-1 language-button"
                            :style="{ borderBottomColor: currentLanguage.value === code ? 'blue': 'transparent', borderBottomWidth: '2px', borderBottomStyle: 'solid' }"
                            @click.prevent="toggleLanguage(code)">
                            {{ code.toUpperCase() }}
                        </a>
                        <span v-if="index < languageCodes.length - 1" class="language-divider">|</span>
                        </span>
                    </div>
                    <div class="col-12 p-0 pt-1 socialLinks" v-if="styleStore?.styles?.linkedinLink?.value || styleStore?.styles?.xLink?.value">
                        <a target="_blank"
                            v-if="styleStore?.styles?.linkedinLink?.value"
                            :href="styleStore.styles?.linkedinLink?.value"
                            class="socialLink linkedinIcon"><i class="fab fa-linkedin"></i></a>
                        <a target="_blank"
                            v-if="styleStore.styles?.xLink?.value"
                            :href="styleStore.styles?.xLink?.value"
                            class="xIcon ml-1 socialLink"><i class="fab fa-x-twitter"></i></a>
                    </div>
                </div>
            </div>
            </div>
            <div class="row">
            <div class="col-12 text-center p-0 mt-4">
                <h1 class="appTitle" v-if="translation?.staticTexts">AI Act Risk Navigator</h1>
                <p class="subheading appSubtitle" v-if="translation?.staticTexts">Classify Your Risk According to the EU AI Act</p>

            </div>
        </div>
    </div>
</template>

<style scoped>
.logoHref:hover {
    text-decoration: none;
    background: unset;
}

.developedBy {
  font-size: clamp(6px, 2vw, 12px);
}

#labLogo {
  max-width: 90px !important;
  width: 100%;
}
</style>